import { ContactCollectorFieldRequirement } from '@graphql/generated-base';
import type { ContactInputFields, FieldSettingsShape } from '../types';

type AddressInput = Readonly<{
  address1: string;
  address2?: string;
  city: string;
  country: string;
  countryCode: string;
  postalCode: string;
  region: string;
  validated: boolean;
}>;

type PhoneNumberInput = Readonly<{
  allowSMS: boolean;
  mobile: boolean;
  number: string;
}>;

const addressFromContact = (
  contact: Pick<ContactInputFields, 'address1' | 'address2' | 'city' | 'country' | 'countryCode' | 'postalCode' | 'region' | 'validated'>,
): AddressInput => ({
  address1: contact.address1,
  address2: contact.address2,
  city: contact.city,
  country: contact.country,
  countryCode: contact.countryCode,
  postalCode: contact.postalCode,
  region: contact.region,
  validated: contact.validated,
});

/**
 * Build the `address` slot of a per-contact submission payload.
 * Address is collected only from the primary (index 0); +1s never submit their own address
 * (matches prod — household members don't carry an address, and the BE treats +1 fields as
 * at-most-optional, so per-contact enforcement never demands one). `OFF` drops it for the
 * primary too, since the UI also hides the section.
 *
 * Blank primary address:
 *  - create (`omitBlankPrimary` true, the default): omitted, so we don't persist empty rows.
 *  - update (`omitBlankPrimary` false): sent as an empty object so a guest who clears the
 *    address actually clears the stored one — mirrors how email clears on update.
 */
export const buildAddress = (contacts: ReadonlyArray<ContactInputFields>, index: number, fs: FieldSettingsShape, omitBlankPrimary = true): AddressInput | undefined => {
  if (fs.address === ContactCollectorFieldRequirement.OFF || index !== 0) return undefined;
  const primary = contacts[0];
  return primary.address1 || !omitBlankPrimary ? addressFromContact(primary) : undefined;
};

/** Email value to send. Returns `''` when OFF so the field is effectively cleared on update paths. */
export const emailFor = (contact: ContactInputFields, fs: FieldSettingsShape): string => (fs.email === ContactCollectorFieldRequirement.OFF ? '' : (contact.email ?? '').trim());

/** Phone numbers slot. `undefined` skips the field (OFF or no value). */
export const phoneNumbersFor = (contact: ContactInputFields, fs: FieldSettingsShape): PhoneNumberInput[] | undefined =>
  fs.phone !== ContactCollectorFieldRequirement.OFF && contact.phoneNumber ? [{ allowSMS: true, mobile: true, number: contact.phoneNumber.trim() }] : undefined;
